import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as mammoth from 'mammoth';

export const exportServices = {
  async toPDF(elementId: string, filename: string = 'cv.pdf') {
    // Helper function for capturing an element off-screen
    const captureElementOffScreen = async (el: HTMLElement): Promise<HTMLCanvasElement> => {
      const clone = el.cloneNode(true) as HTMLElement;

      // Create off-screen container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0px';
      tempContainer.style.width = '842px'; // A4 width approx
      tempContainer.style.overflow = 'visible'; // Cambio: permitir overflow visible
      tempContainer.style.margin = '0';
      tempContainer.style.padding = '0';
      tempContainer.style.border = 'none';
      tempContainer.style.background = 'white';
      tempContainer.style.zIndex = '-1000'; // Asegurar que esté detrás

      // Preservar estilos originales del clone y mejorar la estabilidad
      clone.style.transform = 'none';
      clone.style.width = '842px'; // Ancho fijo en lugar de porcentaje
      clone.style.height = 'auto'; // Altura automática
      clone.style.margin = '0';
      clone.style.padding = '0';
      clone.style.position = 'static'; // Cambio: usar static en lugar de relative
      clone.style.left = 'auto';
      clone.style.top = 'auto';
      clone.style.boxSizing = 'border-box';
      clone.style.display = 'block';
      
      // Función mejorada para copiar TODOS los estilos computados
      const copyAllComputedStyles = (source: Element, target: Element) => {
        const computedStyles = getComputedStyle(source);
        const targetElement = target as HTMLElement;
        
        // Copiar TODAS las propiedades CSS computadas
        for (let i = 0; i < computedStyles.length; i++) {
          const property = computedStyles[i];
          const value = computedStyles.getPropertyValue(property);
          
          // Evitar propiedades que pueden causar problemas
          if (!['cursor', 'pointer-events', 'user-select'].includes(property)) {
            try {
              targetElement.style.setProperty(property, value, 'important');
            } catch (e) {
              // Ignorar errores de propiedades no válidas
            }
          }
        }
        
        // Forzar propiedades específicas que son críticas para el layout
        const criticalOverrides = {
          'box-sizing': computedStyles.boxSizing,
          'display': computedStyles.display,
          'position': computedStyles.position,
          'float': computedStyles.float,
          'clear': computedStyles.clear,
          'width': computedStyles.width,
          'height': computedStyles.height,
          'min-width': computedStyles.minWidth,
          'min-height': computedStyles.minHeight,
          'max-width': computedStyles.maxWidth,
          'max-height': computedStyles.maxHeight,
          'margin': computedStyles.margin,
          'padding': computedStyles.padding,
          'border': computedStyles.border,
          'flex': computedStyles.flex,
          'flex-direction': computedStyles.flexDirection,
          'flex-wrap': computedStyles.flexWrap,
          'justify-content': computedStyles.justifyContent,
          'align-items': computedStyles.alignItems,
          'align-content': computedStyles.alignContent,
          'text-align': computedStyles.textAlign,
          'vertical-align': computedStyles.verticalAlign,
          'line-height': computedStyles.lineHeight,
          'font-family': computedStyles.fontFamily,
          'font-size': computedStyles.fontSize,
          'font-weight': computedStyles.fontWeight,
          'color': computedStyles.color,
          'background': computedStyles.background,
          'background-color': computedStyles.backgroundColor,
          'transform': computedStyles.transform,
          'z-index': computedStyles.zIndex,
          'overflow': computedStyles.overflow,
          'white-space': computedStyles.whiteSpace,
          'word-wrap': computedStyles.wordWrap,
          'word-break': computedStyles.wordBreak
        };
        
        Object.entries(criticalOverrides).forEach(([prop, value]) => {
          if (value && value !== 'auto' && value !== 'normal' && value !== 'none') {
            targetElement.style.setProperty(prop, value, 'important');
          }
        });
      };

              // Forzar recálculo de estilos en elementos hijos
        const allElements = clone.querySelectorAll('*');
        const originalElements = el.querySelectorAll('*');
        
        // Copiar estilos del elemento raíz primero
        copyAllComputedStyles(el, clone);
        
        allElements.forEach((element: any, index: number) => {
          const originalElement = originalElements[index];
          if (originalElement && element.style) {
            // Copiar TODOS los estilos computados
            copyAllComputedStyles(originalElement, element);
          }
        });

      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);

      try {
        // Delay más largo para permitir que todos los estilos se apliquen
        await new Promise(resolve => setTimeout(resolve, 200)); // Aumentado a 200ms

        // Forzar reflow antes de capturar
        tempContainer.offsetHeight;
        clone.offsetHeight;

        // Capture the CLONE inside the off-screen container
        const capturedCanvas = await html2canvas(clone, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 842,
          windowWidth: 842,
          removeContainer: false,
          foreignObjectRendering: false,
          imageTimeout: 15000,
          letterRendering: true, // Mejorar renderizado de texto
          scrollX: 0,
          scrollY: 0,
          x: 0,
          y: 0,
          onclone: (clonedDoc, element) => {
            // Copiar todas las hojas de estilo del documento original
            const originalStyleSheets = Array.from(document.styleSheets);
            originalStyleSheets.forEach((styleSheet) => {
              try {
                if (styleSheet.href) {
                  // Hoja de estilo externa
                  const link = clonedDoc.createElement('link');
                  link.rel = 'stylesheet';
                  link.href = styleSheet.href;
                  clonedDoc.head.appendChild(link);
                } else if (styleSheet.ownerNode) {
                  // Hoja de estilo interna
                  const clonedStyleSheet = styleSheet.ownerNode.cloneNode(true);
                  clonedDoc.head.appendChild(clonedStyleSheet);
                }
              } catch (e) {
                // Ignorar errores de CORS en hojas de estilo externas
                console.warn('No se pudo copiar hoja de estilo:', e);
              }
            });
            
            // Aplicar estilos adicionales al documento clonado
            const clonedBody = clonedDoc.body;
            if (clonedBody) {
              clonedBody.style.margin = '0';
              clonedBody.style.padding = '0';
              clonedBody.style.fontSmooth = 'always';
              clonedBody.style.webkitFontSmoothing = 'antialiased';
              clonedBody.style.mozOsxFontSmoothing = 'grayscale';
            }
            
            // Crear un estilo inline con todas las reglas CSS críticas
            const criticalCSS = clonedDoc.createElement('style');
            criticalCSS.textContent = `
              * {
                box-sizing: border-box !important;
              }
              .flex {
                display: flex !important;
              }
              .flex-col {
                flex-direction: column !important;
              }
              .flex-row {
                flex-direction: row !important;
              }
              .justify-center {
                justify-content: center !important;
              }
              .justify-between {
                justify-content: space-between !important;
              }
              .items-center {
                align-items: center !important;
              }
              .items-start {
                align-items: flex-start !important;
              }
              .text-center {
                text-align: center !important;
              }
              .text-left {
                text-align: left !important;
              }
              .text-right {
                text-align: right !important;
              }
              .relative {
                position: relative !important;
              }
              .absolute {
                position: absolute !important;
              }
              .block {
                display: block !important;
              }
              .inline-block {
                display: inline-block !important;
              }
              .grid {
                display: grid !important;
              }
            `;
            clonedDoc.head.appendChild(criticalCSS);
            
            // Asegurar que todas las fuentes se carguen correctamente
            const allTextElements = element.querySelectorAll('*');
            allTextElements.forEach((el: any) => {
              if (el.style) {
                // Forzar renderizado de fuentes
                const computedStyle = getComputedStyle(el);
                if (computedStyle.fontFamily) {
                  el.style.fontFamily = computedStyle.fontFamily;
                }
                if (computedStyle.fontSize) {
                  el.style.fontSize = computedStyle.fontSize;
                }
                if (computedStyle.fontWeight) {
                  el.style.fontWeight = computedStyle.fontWeight;
                }
                if (computedStyle.lineHeight) {
                  el.style.lineHeight = computedStyle.lineHeight;
                }
                // Mejorar renderizado de texto
                el.style.fontSmooth = 'always';
                el.style.webkitFontSmoothing = 'antialiased';
                el.style.mozOsxFontSmoothing = 'grayscale';
              }
            });
          }
        });
        return capturedCanvas;
      } finally {
        // Clean up: remove the temporary container
        document.body.removeChild(tempContainer);
      }
    };

    try {
      const element = document.getElementById(elementId);
      if (!element) throw new Error(`Element with id ${elementId} not found`);

      // Capture first page using the helper
      const canvas = await captureElementOffScreen(element);

      const a4WidthMm = 210;
      const a4HeightMm = 297;
      const marginMm = 10; // Margen superior/inferior y lateral
      const pageMaxHeightMm = a4HeightMm - (marginMm * 2);
      const pageMaxWidthMm = a4WidthMm - (marginMm * 2);

      let imgWidth, imgHeight, xPos, yPos;

      // Check if it's the main CV export
      if (elementId.startsWith('cv-preview')) {
        // Use original logic for CV: full width, no scaling/centering
        imgWidth = a4WidthMm; // 210
        imgHeight = (canvas.height * imgWidth) / canvas.width;
        xPos = 0;
        yPos = 0;
        console.log(`Exportando CV (${elementId}): Sin escalado ni centrado.`);
      } else {
        // Use scaling and centering logic for other elements (Letter, Analysis)
        imgWidth = pageMaxWidthMm; // Start with max width considering margins
        imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Scale if height exceeds page (only for the first image of non-CV elements)
        if (imgHeight > pageMaxHeightMm) {
          console.log(`Contenido (${elementId}) demasiado alto (${imgHeight.toFixed(2)}mm), escalando para ajustar a ${pageMaxHeightMm}mm.`);
          const scaleFactor = pageMaxHeightMm / imgHeight;
          imgHeight = pageMaxHeightMm;
          imgWidth = imgWidth * scaleFactor;
        }

        // Center the image on the page
        xPos = (a4WidthMm - imgWidth) / 2;
        yPos = marginMm;
        console.log(`Exportando otro elemento (${elementId}): Aplicando escalado/centrado si es necesario.`);
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(canvas.toDataURL('image/jpeg', 1.0), 'JPEG', xPos, yPos, imgWidth, imgHeight);

      // Buscar todas las páginas adicionales (Should only apply for CV)
      let pageIndex = 2;
      let hasMorePages = true;

      while (hasMorePages) {
        let nextPageId = `${elementId}-page${pageIndex}`;
        if (!nextPageId.includes('page')) {
          nextPageId = `cv-preview-page${pageIndex}`;
        }
        const nextPage = document.getElementById(nextPageId);
        console.log(`Buscando página ${pageIndex}:`, nextPageId, nextPage ? 'Encontrada' : 'No encontrada');

        if (nextPage) {
          // Capture additional page using the helper
          const nextCanvas = await captureElementOffScreen(nextPage);

          pdf.addPage();
          // Recalcular dimensiones para esta página específica
          const nextImgWidth = 210; // A4 width in mm
          const nextImgHeight = (nextCanvas.height * nextImgWidth) / nextCanvas.width;
          pdf.addImage(nextCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', 0, 0, nextImgWidth, nextImgHeight);
          pageIndex++;
        } else {
          hasMorePages = false;
        }
      }

      const pdfBlob = pdf.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      return false;
    }
  },

  async toImages(elementId: string) {
    // Helper function for capturing an element off-screen (reutilizada)
    const captureElementOffScreen = async (el: HTMLElement): Promise<HTMLCanvasElement> => {
      const clone = el.cloneNode(true) as HTMLElement;

      // Create off-screen container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0px';
      tempContainer.style.width = '842px';
      tempContainer.style.overflow = 'visible';
      tempContainer.style.margin = '0';
      tempContainer.style.padding = '0';
      tempContainer.style.border = 'none';
      tempContainer.style.background = 'white';
      tempContainer.style.zIndex = '-1000';

      // Preservar estilos originales del clone
      clone.style.transform = 'none';
      clone.style.width = '842px';
      clone.style.height = 'auto';
      clone.style.margin = '0';
      clone.style.padding = '0';
      clone.style.position = 'static';
      clone.style.left = 'auto';
      clone.style.top = 'auto';
      clone.style.boxSizing = 'border-box';
      clone.style.display = 'block';
      
             // Función mejorada para copiar TODOS los estilos computados (reutilizada)
       const copyAllComputedStyles = (source: Element, target: Element) => {
         const computedStyles = getComputedStyle(source);
         const targetElement = target as HTMLElement;
         
         // Copiar TODAS las propiedades CSS computadas
         for (let i = 0; i < computedStyles.length; i++) {
           const property = computedStyles[i];
           const value = computedStyles.getPropertyValue(property);
           
           if (!['cursor', 'pointer-events', 'user-select'].includes(property)) {
             try {
               targetElement.style.setProperty(property, value, 'important');
             } catch (e) {
               // Ignorar errores de propiedades no válidas
             }
           }
         }
         
         // Forzar propiedades específicas críticas
         const criticalOverrides = {
           'box-sizing': computedStyles.boxSizing,
           'display': computedStyles.display,
           'position': computedStyles.position,
           'float': computedStyles.float,
           'clear': computedStyles.clear,
           'width': computedStyles.width,
           'height': computedStyles.height,
           'min-width': computedStyles.minWidth,
           'min-height': computedStyles.minHeight,
           'max-width': computedStyles.maxWidth,
           'max-height': computedStyles.maxHeight,
           'margin': computedStyles.margin,
           'padding': computedStyles.padding,
           'border': computedStyles.border,
           'flex': computedStyles.flex,
           'flex-direction': computedStyles.flexDirection,
           'flex-wrap': computedStyles.flexWrap,
           'justify-content': computedStyles.justifyContent,
           'align-items': computedStyles.alignItems,
           'align-content': computedStyles.alignContent,
           'text-align': computedStyles.textAlign,
           'vertical-align': computedStyles.verticalAlign,
           'line-height': computedStyles.lineHeight,
           'font-family': computedStyles.fontFamily,
           'font-size': computedStyles.fontSize,
           'font-weight': computedStyles.fontWeight,
           'color': computedStyles.color,
           'background': computedStyles.background,
           'background-color': computedStyles.backgroundColor,
           'transform': computedStyles.transform,
           'z-index': computedStyles.zIndex,
           'overflow': computedStyles.overflow,
           'white-space': computedStyles.whiteSpace,
           'word-wrap': computedStyles.wordWrap,
           'word-break': computedStyles.wordBreak
         };
         
         Object.entries(criticalOverrides).forEach(([prop, value]) => {
           if (value && value !== 'auto' && value !== 'normal' && value !== 'none') {
             targetElement.style.setProperty(prop, value, 'important');
           }
         });
       };

       // Forzar recálculo de estilos en elementos hijos
       const allElements = clone.querySelectorAll('*');
       const originalElements = el.querySelectorAll('*');
       
       // Copiar estilos del elemento raíz primero
       copyAllComputedStyles(el, clone);
       
       allElements.forEach((element: any, index: number) => {
         const originalElement = originalElements[index];
         if (originalElement && element.style) {
           // Copiar TODOS los estilos computados
           copyAllComputedStyles(originalElement, element);
         }
       });

      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);

      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        tempContainer.offsetHeight;
        clone.offsetHeight;

                 const capturedCanvas = await html2canvas(clone, {
           scale: 2,
           logging: false,
           useCORS: true,
           allowTaint: true,
           backgroundColor: '#ffffff',
           width: 842,
           windowWidth: 842,
           removeContainer: false,
           foreignObjectRendering: false,
           imageTimeout: 15000,
           letterRendering: true,
           scrollX: 0,
           scrollY: 0,
           x: 0,
           y: 0,
                       onclone: (clonedDoc, element) => {
              // Copiar todas las hojas de estilo del documento original
              const originalStyleSheets = Array.from(document.styleSheets);
              originalStyleSheets.forEach((styleSheet) => {
                try {
                  if (styleSheet.href) {
                    const link = clonedDoc.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = styleSheet.href;
                    clonedDoc.head.appendChild(link);
                  } else if (styleSheet.ownerNode) {
                    const clonedStyleSheet = styleSheet.ownerNode.cloneNode(true);
                    clonedDoc.head.appendChild(clonedStyleSheet);
                  }
                } catch (e) {
                  console.warn('No se pudo copiar hoja de estilo:', e);
                }
              });
              
              const clonedBody = clonedDoc.body;
              if (clonedBody) {
                clonedBody.style.margin = '0';
                clonedBody.style.padding = '0';
                clonedBody.style.fontSmooth = 'always';
                clonedBody.style.webkitFontSmoothing = 'antialiased';
                clonedBody.style.mozOsxFontSmoothing = 'grayscale';
              }
              
              // Crear un estilo inline con todas las reglas CSS críticas
              const criticalCSS = clonedDoc.createElement('style');
              criticalCSS.textContent = `
                * {
                  box-sizing: border-box !important;
                }
                .flex {
                  display: flex !important;
                }
                .flex-col {
                  flex-direction: column !important;
                }
                .flex-row {
                  flex-direction: row !important;
                }
                .justify-center {
                  justify-content: center !important;
                }
                .justify-between {
                  justify-content: space-between !important;
                }
                .items-center {
                  align-items: center !important;
                }
                .items-start {
                  align-items: flex-start !important;
                }
                .text-center {
                  text-align: center !important;
                }
                .text-left {
                  text-align: left !important;
                }
                .text-right {
                  text-align: right !important;
                }
                .relative {
                  position: relative !important;
                }
                .absolute {
                  position: absolute !important;
                }
                .block {
                  display: block !important;
                }
                .inline-block {
                  display: inline-block !important;
                }
                .grid {
                  display: grid !important;
                }
              `;
              clonedDoc.head.appendChild(criticalCSS);
              
              // Asegurar que todas las fuentes se carguen correctamente
              const allTextElements = element.querySelectorAll('*');
              allTextElements.forEach((el: any) => {
                if (el.style) {
                  const computedStyle = getComputedStyle(el);
                  if (computedStyle.fontFamily) {
                    el.style.fontFamily = computedStyle.fontFamily;
                  }
                  if (computedStyle.fontSize) {
                    el.style.fontSize = computedStyle.fontSize;
                  }
                  if (computedStyle.fontWeight) {
                    el.style.fontWeight = computedStyle.fontWeight;
                  }
                  if (computedStyle.lineHeight) {
                    el.style.lineHeight = computedStyle.lineHeight;
                  }
                  el.style.fontSmooth = 'always';
                  el.style.webkitFontSmoothing = 'antialiased';
                  el.style.mozOsxFontSmoothing = 'grayscale';
                }
              });
            }
         });
        return capturedCanvas;
      } finally {
        document.body.removeChild(tempContainer);
      }
    };

    try {
      const images = [];
      
      // Primera página
      const firstElement = document.getElementById(elementId);
      if (firstElement) {
        const canvas1 = await captureElementOffScreen(firstElement);
        images.push(canvas1.toDataURL('image/jpeg', 0.95));
      }

      // Buscar todas las páginas adicionales
      let pageIndex = 2;
      let hasMorePages = true;

      while (hasMorePages) {
        let nextPageId = `${elementId}-page${pageIndex}`;
        if (!nextPageId.includes('page')) {
          nextPageId = `cv-preview-page${pageIndex}`;
        }
        const nextPage = document.getElementById(nextPageId);

        if (nextPage) {
          const canvas = await captureElementOffScreen(nextPage);
          images.push(canvas.toDataURL('image/jpeg', 0.95));
          pageIndex++;
        } else {
          hasMorePages = false;
        }
      }

      // Descargar imágenes
      images.forEach((dataUrl, index) => {
        const blob = this.dataURLtoBlob(dataUrl);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cv-page${index + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });

      return true;
    } catch (error) {
      console.error('Error exporting to images:', error);
      return false;
    }
  },

  toWord: async (data: any, filename: string) => {
    try {
      // Si es una carta de presentación, formateamos diferente
      const isLetter = filename.startsWith('carta-');
      
      let docContent = '';
      
      if (isLetter) {
        // Formato para carta de presentación
        const letterContent = typeof data === 'string' ? JSON.parse(data) : data;
        docContent = `
          <html>
            <head>
              <meta charset="UTF-8">
              <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; }
                .header { margin-bottom: 20px; }
                .content { margin: 20px 0; }
                .signature { margin-top: 30px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>${letterContent.name || ''}</h1>
                <p>${letterContent.title || ''}</p>
                <p>${letterContent.contact?.location || ''}</p>
                <p>${letterContent.contact?.phone || ''}</p>
                <p>${letterContent.contact?.email || ''}</p>
                <p>${new Date().toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</p>
              </div>
              <div class="content">
                <p>Estimado/a Equipo de Recursos Humanos:</p>
                ${letterContent.paragraphs.map((p: string) => `<p>${p}</p>`).join('\n')}
              </div>
              <div class="signature">
                <p>Atentamente,</p>
              </div>
            </body>
          </html>
        `;
      }

      // Rest of the function remains unchanged
      // ...
    } catch (error) {
      console.error('Error exporting to Word:', error);
      return false;
    }
  },

  dataURLtoBlob: (dataUrl: string): Blob => {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ia], { type: mimeString });
    return blob;
  }
};